import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import ShareModal from "@/src/components/action-modals/share-modal";
import AllNewsGrid from "@/src/components/news/all-news/all-news-grid";
import GlobalNewsCard from "@/src/components/news/global-news-cards";
import CategoryTags from "@/src/components/news/latest-news/category-tags";
import NewsCard from "@/src/components/news/latest-news/latest-news-card";
import NewsListScrollable from "@/src/components/news/latest-news/news-list";
import { LatestNewsSkeleton } from "@/src/components/news/loading-skeleton/latest-news-skeleton";
import PermissionDenied from "@/src/components/ui-kit/permission-denied";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import ScTabs from "@/src/components/ui-kit/tabs";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import {
  formatArticleDate,
  NewsTab,
  resolveAuthorName,
  useLatestNews,
} from "@/src/hooks/features/news/use-latest-news";
import { usePermissions } from "@/src/hooks/use-permissions";
import { useResponsiveGap } from "@/src/hooks/use-responsive-gap";
import { BlocksApiNews } from "@/src/models/news.model";
import { router } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";

export default function LatestNewsScreen() {
  const { t } = useTranslation();
  const gap = useResponsiveGap();

  const {
    searchText,
    setSearchText,
    activeTab,
    setActiveTab,
    deleteTarget,
    setDeleteTarget,
    isMobile,
    tabs,
    isAdmin,
    globalNewsData,
    isLoadingGlobalNews,
    isGlobalNewsError,
    activeNews,
    featuredArticle,
    remainingLatestArticles,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    visibleCategoryNames,
    isLoading,
    isSaved,
    isOwner,
    toggleSave,
    togglePin,
    confirmDelete,
    handleEdit,
    handleCategoryPress,
    resolveArticleCategoryNames,
  } = useLatestNews();

  const [shareTarget, setShareTarget] = useState<BlocksApiNews | null>(null);

  const { canCreate, canView } = usePermissions();
  const canCreateNews = isAdmin && canCreate("news");
  const canViewNews = canView("news");

  const gridVariant =
    activeTab === NewsTab.DRAFT_NEWS
      ? "draft"
      : activeTab === NewsTab.SAVED_NEWS
        ? "saved"
        : activeTab === NewsTab.PINNED_NEWS
          ? "pinned"
          : "all";

  const isGridTab =
    activeTab === NewsTab.ALL_NEWS ||
    activeTab === NewsTab.DRAFT_NEWS ||
    activeTab === NewsTab.SAVED_NEWS ||
    activeTab === NewsTab.PINNED_NEWS;

  if (!canViewNews) {
    return (
      <View className={`flex-row w-full flex-1 bg-obsidian ${gap}`}>
        <PermissionDenied />
      </View>
    );
  }

  return (
    <View className={`flex-row w-full flex-1 bg-obsidian ${gap}`}>
      <View className="w-full md:w-[68%] flex-col gap-4">
        <View className="md:border md:border-ash-3 md:bg-chalk md:rounded-2xl">
          <View className="flex-row items-center justify-between gap-4 pb-3 md:border-b md:border-ash-3 md:p-4">
            <View className="flex-1 md:flex-none md:w-[50%]">
              <ScSearchInput
                value={searchText}
                onChangeText={setSearchText}
                placeholderKey="LABEL_SEARCH_NEWS"
                containerClassName="rounded-full"
                surface="screen"
                onCreatePress={
                  canCreateNews
                    ? () => router.push("/(tabs)/news/news-creation")
                    : undefined
                }
              />
            </View>
            {canCreateNews && (
              <ToolbarCreateButton
                text="BTN_CREATE_NEWS"
                onPress={() => router.push("/(tabs)/news/news-creation")}
                hideOnMobile
              />
            )}
          </View>

          <View className="mt-1 justify-center pb-2 md:mt-4 md:px-4 md:pb-4">
            <ScTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
          </View>
        </View>

        <View className="flex-1 flex-col md:border md:border-ash-3 md:bg-chalk md:rounded-2xl md:p-4">
          <View className="hidden md:flex flex-row items-center justify-between mb-4">
            <Text className="text-sm font-bold text-onyx">
              {activeTab === NewsTab.LATEST_NEWS
                ? t("LABEL_LATEST_NEWS")
                : activeTab === NewsTab.ALL_NEWS
                  ? t("LABEL_ALL_NEWS")
                  : activeTab === NewsTab.DRAFT_NEWS
                    ? t("LABEL_DRAFT_NEWS")
                    : t("LABEL_SAVED_NEWS")}
            </Text>
          </View>

          {activeTab !== NewsTab.DRAFT_NEWS && (
            <View className="mb-1">
              <CategoryTags
                categories={visibleCategoryNames}
                onCategoryPress={handleCategoryPress}
              />
            </View>
          )}

          {isGridTab ? (
            <AllNewsGrid
              articles={activeNews}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              searchText={searchText}
              variant={gridVariant}
              isSaved={isSaved}
              isOwner={isOwner}
              resolveArticleCategoryNames={resolveArticleCategoryNames}
              onSave={(itemId) => toggleSave(itemId)}
              onEdit={handleEdit}
              onDelete={(newsItem) => setDeleteTarget(newsItem)}
              onEndReached={fetchNextPage}
              onPin={(newsItem) => togglePin(newsItem)}
            />
          ) : isLoading ? (
            <LatestNewsSkeleton isMobile={isMobile} />
          ) : activeTab === NewsTab.LATEST_NEWS && featuredArticle ? (
            <View
              className="flex-row flex-1 mt-4 gap-4"
              style={{ minHeight: 0, overflow: "hidden" }}
            >
              <View className="flex-1">
                <NewsCard
                  variant="large"
                  title={featuredArticle.Title}
                  date={formatArticleDate(featuredArticle.CreatedDate)}
                  tags={resolveArticleCategoryNames(featuredArticle)}
                  authorName={resolveAuthorName(featuredArticle)}
                  authorRole={t("LABEL_AUTHOR")}
                  authorId={featuredArticle.CreatedBy}
                  authorImage={
                    featuredArticle.authorSnapshot?.profileImageUrl
                      ? { uri: featuredArticle.authorSnapshot.profileImageUrl }
                      : undefined
                  }
                  content={featuredArticle.Content}
                  imageUrls={
                    featuredArticle.coverImageUrl
                      ? { uri: featuredArticle.coverImageUrl }
                      : undefined
                  }
                  isSaved={isSaved(featuredArticle.ItemId)}
                  isOwner={isOwner(featuredArticle)}
                  isPinned={featuredArticle.isPinned}
                  onPress={() => {
                    router.push({
                      pathname: "/(tabs)/news/news-detail",
                      params: { newsId: featuredArticle.ItemId },
                    });
                  }}
                  onSave={() => toggleSave(featuredArticle.ItemId)}
                  onShare={() => setShareTarget(featuredArticle)}
                  onEdit={() => handleEdit(featuredArticle)}
                  onDelete={() => setDeleteTarget(featuredArticle)}
                  onPin={() => togglePin(featuredArticle)}
                />
              </View>

              <NewsListScrollable>
                {remainingLatestArticles.map((newsItem) => (
                  <NewsCard
                    variant="small"
                    key={newsItem.ItemId}
                    title={newsItem.Title}
                    date={formatArticleDate(newsItem.CreatedDate)}
                    tags={resolveArticleCategoryNames(newsItem)}
                    imageUrls={
                      newsItem.coverImageUrl
                        ? { uri: newsItem.coverImageUrl }
                        : undefined
                    }
                    content={newsItem.Content}
                    isSaved={isSaved(newsItem.ItemId)}
                    isPinned={newsItem.isPinned}
                    isOwner={isOwner(newsItem)}
                    onPress={() =>
                      router.push({
                        pathname: "/(tabs)/news/news-detail",
                        params: { newsId: newsItem.ItemId },
                      })
                    }
                    onSave={() => toggleSave(newsItem.ItemId)}
                    onShare={() => setShareTarget(newsItem)}
                    onEdit={() => handleEdit(newsItem)}
                    onDelete={() => setDeleteTarget(newsItem)}
                    onPin={() => togglePin(newsItem)}
                  />
                ))}
              </NewsListScrollable>
            </View>
          ) : activeNews.length === 0 ? (
            <View className="flex-1 items-center justify-center">
              <Text className="text-slate text-sm">{t("LABEL_NO_NEWS")}</Text>
            </View>
          ) : (
            <NewsListScrollable maxHeight={440}>
              {activeNews.map((newsItem) => (
                <NewsCard
                  variant="small"
                  key={newsItem.ItemId}
                  title={newsItem.Title}
                  date={formatArticleDate(newsItem.CreatedDate)}
                  tags={resolveArticleCategoryNames(newsItem)}
                  imageUrls={
                    newsItem.coverImageUrl
                      ? { uri: newsItem.coverImageUrl }
                      : undefined
                  }
                  content={newsItem.Content}
                  isSaved={isSaved(newsItem.ItemId)}
                  isPinned={newsItem.isPinned}
                  isOwner={isOwner(newsItem)}
                  onPress={() =>
                    router.push({
                      pathname: "/(tabs)/news/news-detail",
                      params: { newsId: newsItem.ItemId },
                    })
                  }
                  onSave={() => toggleSave(newsItem.ItemId)}
                  onShare={() => setShareTarget(newsItem)}
                  onEdit={() => handleEdit(newsItem)}
                  onDelete={() => setDeleteTarget(newsItem)}
                  onPin={() => togglePin(newsItem)}
                />
              ))}
            </NewsListScrollable>
          )}
        </View>
      </View>

      {!isMobile && (
        <GlobalNewsCard
          data={globalNewsData}
          isLoading={isLoadingGlobalNews}
          isError={isGlobalNewsError}
        />
      )}

      <ConfirmModal
        visible={deleteTarget !== null}
        title="BTN_DELETE"
        description="LABEL_DESC_DELETE_NEWS"
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ShareModal
        visible={shareTarget !== null}
        onClose={() => setShareTarget(null)}
        resourceId={shareTarget?.ItemId ?? ""}
        resourcePath="/(tabs)/news/latest-news"
        title={shareTarget?.Title}
      />
    </View>
  );
}
